import React, { useContext, useEffect } from "react";
import { observer } from "mobx-react";

import { StoresContext } from "../../../../pages/_app";
import {
  NODE_CATALOG_NUMBER,
  NODE_CHILDREN,
  NODE_IDX,
  NODE_NAME,
  NODE_PARENT,
  NODE_RANK,
  TreeNode,
} from "../../../../stores/catalog-tree-store";
import UnexpectedBackendError from "../../../backend-error";
import Loading from "../../../loading";
import { NoResults } from "../no-results";
import { GenomesTreeEdge } from "./edge";
import { RIGHT_PADDING_WITHOUT_LEAVES, RIGHT_PADDING_WITH_LEAVES, X_GAP, Y_GAP } from "./constants";
import { GenomesTreeNodeName } from "./node-name";
import { GenomesTreeNodeRank } from "./node-rank";
import { GenomesTreeBreadcrumbs } from "./breadcrumbs";
import { GenomesTreeNodeTags } from "./node-tags";
import { catalogItemDisplayName } from "../../../../utils/genomes";
import { GenomesTreeDataItemsWithCatalogNumbers } from "./items-with-catalog-numbers";

export type Position = [number, number];
export type Positions = { [key: number]: Position };
export type WithoutResults = { [key: number]: { pos: Position; num: number } };

function getPositions(
  visibleIndexes: number[],
  nodes: TreeNode[],
  isNodePartOfResults: (idx: number) => boolean
): { positions: Positions; withoutResults: WithoutResults; maxX: number; maxY: number } {
  const positions: Positions = {};
  const withoutResults: WithoutResults = {};

  for (const idx of visibleIndexes) {
    positions[idx] = [0, 0];
  }

  let yPos = 0;
  let maxX = 0;
  let anyVisibleLeaves = false;
  function iter(nodeIdx: number, depth: number) {
    const node = nodes[nodeIdx];
    if (node[NODE_CHILDREN].length === 0) {
      anyVisibleLeaves = true;
    }
    const pos = positions[nodeIdx];

    pos[0] = X_GAP * depth;
    if (pos[0] > maxX) {
      maxX = pos[0];
    }
    pos[1] = yPos;
    yPos += Y_GAP;
    let numVisibleChildren = 0;
    let numPartOfResults = 0;
    for (const childIdx of node[NODE_CHILDREN]) {
      if (visibleIndexes.indexOf(childIdx) !== -1) {
        numVisibleChildren++;
        iter(childIdx, depth + 1);
      } else if (isNodePartOfResults(childIdx)) {
        numPartOfResults++;
      }
    }
    const numHidden = node[NODE_CHILDREN].length - numVisibleChildren - numPartOfResults;
    if (numHidden > 0) {
      const pos: [number, number] = [X_GAP * (depth + 1), yPos];
      withoutResults[nodeIdx] = { num: numHidden, pos };
      if (pos[0] > maxX) {
        maxX = pos[0];
      }
      yPos += Y_GAP;
    }
  }

  if (visibleIndexes.length > 0) {
    iter(visibleIndexes[0], 0);
  }

  maxX += anyVisibleLeaves ? RIGHT_PADDING_WITH_LEAVES : RIGHT_PADDING_WITHOUT_LEAVES;

  return { positions, withoutResults, maxX, maxY: yPos };
}

const GenomesTree = observer(() => {
  const rootStore = useContext(StoresContext);
  const {
    nodes,
    isInResults,
    getNumResultsWithin,
    getNumWithUuidsWithin,
    toggleNode,
    openAllChildren,
    isPartOfCatalog,
    isDerivative,
    getUuid,
  } = rootStore.catalogTreeStore;
  const { isError, isLoading, visibleIndexes } = rootStore.catalogTreeStore.getVisibleIndexes();

  useEffect(() => {
    if (!isLoading) {
      rootStore.genomeStore.selectAll();
    }

    return () => {
      rootStore.genomeStore.deselectAll();
    };
  }, [isLoading]);

  if (isError) {
    return <UnexpectedBackendError />;
  }
  if (isLoading) {
    return <Loading text="Loading..." />;
  }
  if (visibleIndexes.length === 0) {
    return <NoResults />;
  }

  const catalogNumberParents = new Set<number>(
    visibleIndexes.filter((idx) => isPartOfCatalog(idx)).map((idx) => nodes[idx][NODE_PARENT]!)
  );

  const { positions, maxY, maxX, withoutResults } = getPositions(visibleIndexes, nodes, (idx) => {
    return isInResults(idx) || getNumResultsWithin(idx) > 0;
  });

  return (
    <div className="genomes-tree">
      <GenomesTreeBreadcrumbs nodes={nodes} visibleIndexes={visibleIndexes} />
      <div className="genomes-tree__nodes" style={{ minWidth: `${maxX}px` }}>
        <svg height={`${maxY + 40 * 2}px`} xmlns="http://www.w3.org/2000/svg">
          <g transform={"translate(40 40)"}>
            {[...catalogNumberParents].map((idx) => (
              <GenomesTreeDataItemsWithCatalogNumbers
                key={idx}
                nodes={nodes}
                positions={positions}
                isDerivative={isDerivative}
                getUuid={getUuid}
                indexes={nodes[idx][NODE_CHILDREN].filter(
                  (childIdx) =>
                    nodes[childIdx][NODE_CHILDREN].length === 0 &&
                    visibleIndexes.indexOf(childIdx) !== -1
                )}
              />
            ))}
            {visibleIndexes.map((parentIdx) => (
              <React.Fragment key={nodes[parentIdx][NODE_IDX]}>
                {nodes[parentIdx][NODE_CHILDREN].filter(
                  (childIdx) => visibleIndexes.indexOf(childIdx) !== -1
                )
                  .filter((childIdx) => !isDerivative(childIdx))
                  .map(
                    (childIdx) =>
                      [childIdx, positions[parentIdx], positions[childIdx]] as [
                        number,
                        Position,
                        Position
                      ]
                  )
                  .map(([childIdx, p0, p1]) => (
                    <GenomesTreeEdge key={childIdx} p0={p0} p1={p1} />
                  ))}
              </React.Fragment>
            ))}
            {visibleIndexes
              .filter((idx) => withoutResults[idx] !== undefined)
              .map((idx) => (
                <GenomesTreeEdge
                  key={idx}
                  p0={positions[idx]}
                  p1={withoutResults[idx].pos}
                  muted={true}
                />
              ))}
            {visibleIndexes.map((idx) => (
              <React.Fragment key={nodes[idx][NODE_IDX]}>
                <GenomesTreeNodeName
                  position={positions[idx]}
                  hasDownloadsWithin={getNumWithUuidsWithin(idx) > 0}
                  hasDownloads={getUuid(idx) !== null}
                  isDerivative={isDerivative(idx)}
                  isLink={nodes[idx][NODE_CHILDREN].length === 0}
                  onClick={
                    nodes[idx][NODE_CHILDREN].length > 0
                      ? () => toggleNode(idx)
                      : () => {
                          const catalogNumber = nodes[idx][NODE_CATALOG_NUMBER];
                          window.open(
                            `https://www.atcc.org/products/${catalogNumber?.toLowerCase()}`,
                            "_blank"
                          );
                        }
                  }
                >
                  {catalogItemDisplayName(nodes[idx][NODE_NAME], nodes[idx][NODE_CATALOG_NUMBER])}
                  {getNumResultsWithin(idx) > 0 ? (
                    <>
                      {" "}
                      <tspan
                        className="genomes-tree__nodes__item__num-results"
                        dominantBaseline="middle"
                      >
                        ({getNumResultsWithin(idx)} results)
                      </tspan>
                    </>
                  ) : null}
                </GenomesTreeNodeName>
                <GenomesTreeNodeRank p={positions[idx]} rank={nodes[idx][NODE_RANK]} />
                <GenomesTreeNodeTags
                  p={positions[idx]}
                  tags={rootStore.catalogTreeStore.getTags(idx)}
                />
                {withoutResults[idx] !== undefined ? (
                  <GenomesTreeNodeName
                    position={withoutResults[idx].pos}
                    isMuted={true}
                    onClick={() => openAllChildren(idx)}
                  >
                    <WithoutResultsMessage
                      idx={idx}
                      nodes={nodes}
                      numHidden={withoutResults[idx].num}
                    />
                  </GenomesTreeNodeName>
                ) : null}
              </React.Fragment>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
});

const WithoutResultsMessage = ({
  numHidden,
  nodes,
  idx,
}: {
  numHidden: number;
  nodes: TreeNode[];
  idx: number;
}) => {
  if (nodes[idx][NODE_CHILDREN].length > 0) {
    const childIdx = nodes[idx][NODE_CHILDREN][0]!;
    const child = nodes[childIdx];
    const childRank = child[NODE_RANK];
    if (childRank !== "no rank") {
      return (
        <>
          {numHidden} other {childRank} in {nodes[idx][NODE_NAME]} without results
        </>
      );
    }
  }

  return (
    <>
      {numHidden} other in {nodes[idx][NODE_NAME]} without results
    </>
  );
};

export default GenomesTree;
